import { useEffect, useMemo, useRef, useState } from "react";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import type { CompanyPurchasePaymentChallengeProps } from "./companyPurchaseContracts";

/** PaymentIntent only. Never mounts a personal/default-key or SetupIntent path. */
export function CompanyPurchasePaymentChallenge(props: CompanyPurchasePaymentChallengeProps) {
  const stripe = useMemo(() => loadStripe(props.challenge.publishable_key), [props.challenge.publishable_key]);
  const options = useMemo(() => ({ clientSecret: props.challenge.client_secret }), [props.challenge.client_secret]);
  return <Elements key={props.challenge.operation_id} stripe={stripe} options={options}>
    <OriginalPayment {...props} />
  </Elements>;
}
function OriginalPayment({ challenge, onReconcile, onDismiss, onBusyChange }: CompanyPurchasePaymentChallengeProps) {
  const stripe = useStripe(), live = useRef(true), inFlight = useRef(false);
  const [busy, setBusy] = useState(false), [error, setError] = useState(false);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  async function confirm() {
    if (!stripe || inFlight.current) return;
    inFlight.current = true; setBusy(true); onBusyChange(true); setError(false);
    try {
      // The server already bound this intent to the authorized company card.
      // Authenticate that original method; never collect or substitute a new one.
      try {
        const result = await stripe.confirmCardPayment(challenge.client_secret);
        if (live.current && (result.error || !result.paymentIntent)) setError(true);
      }
      catch { if (live.current) setError(true); }
      if (live.current) await onReconcile(challenge.purchase_id);
    } catch { if (live.current) setError(true); }
    finally { inFlight.current = false; if (live.current) setBusy(false); onBusyChange(false); }
  }
  return <Stack spacing={1.5}>
    {error ? <Alert severity="warning">Payment confirmation is unresolved. Rejoin the original purchase to check its status.</Alert> : null}
    <Stack direction="row" spacing={1}>
      <Button size="small" variant="contained" disabled={!stripe || busy} onClick={() => void confirm()}>Confirm original payment</Button>
      <Button size="small" disabled={busy} onClick={onDismiss}>Dismiss</Button>
    </Stack>
  </Stack>;
}
